import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, Phone } from "lucide-react";

// Cal.com booking link
const CALENDLY_LINK = "https://cal.com/modern-path-ai-pludn4";

// Industry categories with their pages
const industryCategories = {
  "Home & Property": [
    { name: "Home Services", slug: "home-services" },
    { name: "Appliance Repair", slug: "appliance-repair" },
    { name: "Garage Door", slug: "garage-door" },
    { name: "Locksmith", slug: "locksmith" },
    { name: "Pest Control", slug: "pest-control" },
    { name: "Landscaping", slug: "landscaping" },
  ],
  "Auto & Mechanical": [
    { name: "Auto Repair", slug: "auto-repair" },
    { name: "Towing Services", slug: "towing" },
    { name: "Auto Detailing", slug: "auto-detailing" },
  ],
  "Professional Services": [
    { name: "Law Firms", slug: "legal" },
    { name: "Accounting", slug: "accounting" },
    { name: "Real Estate", slug: "real-estate" },
  ],
  "Health & Wellness": [
    { name: "Beauty & Wellness", slug: "beauty" },
    { name: "Pet Care", slug: "pet-care" },
    { name: "Dental Clinics", slug: "dental" },
    { name: "Fitness Studios", slug: "fitness" },
  ],
};

// Flatten industries for mobile menu
const allIndustries = Object.values(industryCategories).flat();

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsSolutionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSolutionsOpen(false);
  }, [location]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-[#0A0A0A]/95 backdrop-blur-md border-b border-white/5"
            : "bg-transparent"
        }`}
        data-testid="main-header"
      >
        <div className="max-w-[1400px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              className="text-xl font-bold tracking-tight text-white hover:text-[#00FFFF] transition-colors"
              style={{ fontFamily: "Syne, sans-serif" }}
              data-testid="header-logo"
            >
              MODERN PATH AI
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors ${
                  location.pathname === "/" ? "text-[#00FFFF]" : "text-[#A0A0A0] hover:text-white"
                }`}
              >
                Home
              </Link>

              {/* Solutions Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsSolutionsOpen(!isSolutionsOpen)}
                  className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                    location.pathname.startsWith("/industries")
                      ? "text-[#00FFFF]"
                      : "text-[#A0A0A0] hover:text-white"
                  }`}
                  data-testid="solutions-dropdown-btn"
                  aria-expanded={isSolutionsOpen}
                  aria-haspopup="true"
                >
                  Solutions
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      isSolutionsOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <AnimatePresence>
                  {isSolutionsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[600px] bg-[#121212]/98 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden"
                      data-testid="solutions-dropdown"
                    >
                      <div className="p-6">
                        <p className="text-xs font-mono uppercase tracking-widest text-[#00FFFF] mb-4">
                          Industry Solutions
                        </p>
                        <div className="grid grid-cols-2 gap-6">
                          {Object.entries(industryCategories).map(([category, industries]) => (
                            <div key={category}>
                              <p className="text-xs font-semibold text-[#606060] uppercase tracking-wider mb-2">
                                {category}
                              </p>
                              <ul className="space-y-1">
                                {industries.map((industry) => (
                                  <li key={industry.slug}>
                                    <Link
                                      to={`/industries/${industry.slug}`}
                                      className="block py-1.5 px-2 -mx-2 text-sm text-[#A0A0A0] hover:text-white hover:bg-white/5 rounded transition-colors"
                                      onClick={() => setIsSolutionsOpen(false)}
                                    >
                                      {industry.name}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="bg-white/[0.02] border-t border-white/5 p-4">
                        <Link
                          to="/industries/home-services"
                          className="text-sm text-[#00FFFF] hover:underline"
                          onClick={() => setIsSolutionsOpen(false)}
                        >
                          View All Solutions →
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link
                to="/privacy"
                className={`text-sm font-medium transition-colors ${
                  location.pathname === "/privacy"
                    ? "text-[#00FFFF]"
                    : "text-[#A0A0A0] hover:text-white"
                }`}
              >
                Privacy
              </Link>

              <a
                href={`tel:+16473707545`}
                className="flex items-center gap-2 text-sm text-[#A0A0A0] hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4" />
                647-370-7545
              </a>
            </nav>

            {/* CTA Button */}
            <div className="hidden md:block">
              <a
                href={CALENDLY_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#00FFFF] text-[#0A0A0A] px-5 py-2.5 rounded-sm text-sm font-bold uppercase tracking-wide hover:bg-white transition-colors"
                data-testid="header-cta"
              >
                Book a Demo
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-white"
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#0A0A0A] pt-20 overflow-y-auto md:hidden"
            data-testid="mobile-menu"
          >
            <div className="p-4 space-y-4">
              <Link
                to="/"
                className="block py-3 text-lg font-medium text-white border-b border-white/10"
              >
                Home
              </Link>

              <div className="py-3 border-b border-white/10">
                <p className="text-xs font-mono uppercase tracking-widest text-[#00FFFF] mb-3">
                  Solutions
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {allIndustries.map((industry) => (
                    <Link
                      key={industry.slug}
                      to={`/industries/${industry.slug}`}
                      className="py-2 px-3 text-sm text-[#A0A0A0] hover:text-white hover:bg-white/5 rounded transition-colors"
                    >
                      {industry.name}
                    </Link>
                  ))}
                </div>
              </div>

              <Link
                to="/privacy"
                className="block py-3 text-lg font-medium text-white border-b border-white/10"
              >
                Privacy Policy
              </Link>

              <a
                href={`tel:+16473707545`}
                className="flex items-center gap-2 py-3 text-lg text-white border-b border-white/10"
              >
                <Phone className="w-5 h-5" />
                647-370-7545
              </a>

              <a
                href={CALENDLY_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center bg-[#00FFFF] text-[#0A0A0A] py-4 rounded-sm font-bold uppercase tracking-wide mt-6"
              >
                Book a Demo
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
export { industryCategories, allIndustries, CALENDLY_LINK };
